import { cn } from "@/libs/utils";
import {
  getActiveMenuName,
  type MenuName,
  getNavigationMenus,
  type Menu,
  type MenuGroup,
  type PanelKey,
} from "@/v2-components/global-navigation-bar/_const/navigation-menus";
import { Portal } from "@radix-ui/react-portal";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import { Dot } from "@/v2-components/dot";
import { NavigationMenuContent } from "@/v2-components/global-navigation-bar/navigation-menu-content";
import ArrowDropDownIcon from "@/v2-components/core/icon/arrow-drop-down-icon";
import { useLocale } from "@/store/locale-store";
import { useTrans } from "@/utils/useTrans";

/**
 * 데스크탑 GNB 2deps — **공유 시트(단일 면) + 높이 트랜지션** 구조.
 *
 * ## 왜 Radix `HoverCard` 를 안 쓰나
 *
 * 시안의 「메뉴 간 전환」 모션(시트가 닫히지 않고 높이만 이어서 변형)은 독립 드롭다운
 * 4개로는 **원리적으로 나오지 않는다.** 배경·보더·그림자를 갖는 요소가 메뉴마다 따로
 * 있으면 전환할 때 면이 두 겹으로 보이거나 "닫혔다 다시 열린" 것처럼 읽힌다.
 * 그래서 **면은 시트 하나뿐**이고, 패널 4개는 그 위에 절대 위치로 겹쳐 `opacity` /
 * `visibility` 만 토글한다.
 *
 * ## 패널 4개를 **함께** 마운트하는 이유 (그리고 상시가 아닌 이유)
 *
 * 시트 높이를 트랜지션하려면 대상 패널의 **실제 높이를 px 로** 알아야 한다(`height: auto`
 * 는 트랜지션되지 않는다). 그래서 열려 있는 패널만 렌더할 수 없다 — 「메뉴 간 전환」에서
 * 다음 패널의 높이를 미리 재야 하므로 4개가 같이 올라와 있어야 하고, 숨김은
 * `display: none` 이 아니라 **`visibility: hidden`** 이다(`display:none` 이면
 * `offsetHeight` 가 0 이라 강제 리플로 해킹이 필요해진다).
 *
 * 다만 **상시는 아니다.** 네비에 포인터·포커스가 들어온 동안만 올린다(`armed`) — 이유는
 * 그 state 의 주석 참고.
 *
 * ## 트리거가 두 묶음으로 갈린다
 *
 * 시안은 6개를 한 덩어리로 두지 않는다 — **좌 3(로고 옆) / 우 3(아이콘 옆)** 이다.
 * 그런데 **시트와 패널은 하나뿐이어야** 한다(그게 이 모션의 전제다). 그래서 컴포넌트를
 * 셋으로 나눈다:
 *
 * - `NavigationMenuProvider` — 상태 소유. 바 루트에 한 번.
 * - `NavigationMenuTriggers group="left" | "right"` — 트리거만. 두 번.
 * - `NavigationMenuPanels` — 시트 + 패널 4개. 바 루트에 한 번.
 *
 * ⚠️ `NavigationMenuTriggers` 를 두 번 렌더한다고 시트가 둘이 되지 않게, 시트는 트리거와
 * **다른 컴포넌트**에 있다. 트리거 안에 시트를 두면 면이 두 겹이 된다.
 *
 * ## 스태킹 (docs/pitfalls.md #31(헤더 밖으로 넘치는 요소) · #32(헤더 자손 돌출 hover dead zone))
 *
 * 시트·패널은 **GNB 바의 absolute 자식**이다(컨테이닝 블록 = `index.tsx` 루트의
 * `relative`). 레이어 스케일은 그 문서의 규칙을 그대로 따른다:
 * 딤 `z-20` < **시트·패널 `z-[25]`** < 스티키 헤더 래퍼 `z-30`(`PageTemplate` 만 `z-40`).
 *
 * - 시트를 헤더보다 위로 올리면 **헤더 밖으로 넘치는 쿠폰 말풍선이 조용히 잘린다**(#31).
 * - 쿠폰 말풍선(`z-40`)이 시트보다 위인 것은 **의도**다. 말풍선 박스는 우측 끝 고정이고
 *   꼬리는 `pointer-events-none` 이라 네비 → 시트로 내려가는 통로를 막지 않는다(#32).
 *   말풍선을 **가로로 옮기면 그 자체가 새 dead zone** 이 되므로 옮기지 말 것.
 */

/** `mouseleave` 후 닫기까지의 지연. 없으면 네비→시트로 내려가는 사이에 시트가 깜빡인다. */
const CLOSE_DELAY_MS = 120;

type Ctx = {
  menus: Menu[];
  activeMenuName: ReturnType<typeof getActiveMenuName>;
  openPanel: PanelKey | null;
  open: (key: PanelKey) => void;
  close: () => void;
  closeSoon: () => void;
  cancelClose: () => void;
  arm: () => void;
  armed: boolean;
  switching: boolean;
  sheetHeight: number;
  panelRefs: React.MutableRefObject<Map<PanelKey, HTMLDivElement | null>>;
};

const NavigationMenuContext = React.createContext<Ctx | null>(null);

export const useNavigationMenu = () => {
  const ctx = React.useContext(NavigationMenuContext);
  if (!ctx) {
    throw new Error(
      "NavigationMenuTriggers/Panels 는 NavigationMenuProvider 안에서만 렌더할 수 있다",
    );
  }
  return ctx;
};

export function NavigationMenuProvider({
  children,
  activeMenuOverride,
}: React.PropsWithChildren<{
  /**
   * 경로로 활성 메뉴를 못 가르는 화면이 넘긴다 — 지금은 **게임 상세 1곳**.
   * 사유는 `getActiveMenuName` 주석.
   */
  activeMenuOverride?: MenuName;
}>) {
  const router = useRouter();
  const { languageCode } = useLocale();

  const navigationMenus = getNavigationMenus(languageCode);
  const panelMenus = navigationMenus.filter(
    (menu): menu is Menu & { panel: PanelKey } => !!menu.panel,
  );
  const activeMenuName = getActiveMenuName(
    navigationMenus,
    router.pathname,
    activeMenuOverride,
  );

  /**
   * 패널을 DOM 에 올려 둘지. **네비에 포인터·포커스가 들어왔을 때만** 마운트한다.
   *
   * 상시 마운트하면 (a) 유휴 상태에서도 패널 4개의 링크가 헤더 아래에 **기하학적으로**
   * 남아, 스티키 헤더가 스크롤로 숨은 뒤에도 "헤더 요소가 화면에 남아 있음"으로 잡히고
   * (`tests/games-sticky-tab.spec.ts` · docs/pitfalls.md #36(스크롤로 숨는 스티키 헤더)), (b) 메뉴를 한 번도 안 여는
   * 페이지에서까지 제품·게임 목록을 렌더한다(GNB 는 전 페이지 템플릿 10곳에 있다).
   *
   * 높이 측정은 여전히 가능하다 — 열기 **전에** 마운트되고, 마운트 직후 레이아웃 효과가
   * 실제 `offsetHeight` 로 시트 높이를 맞춘다.
   */
  const [armed, setArmed] = React.useState(false);
  const [openPanel, setOpenPanel] = React.useState<PanelKey | null>(null);
  /** 열림→열림 전환이면 진입 슬라이드를 뺀다. 닫힘→열림에서만 슬라이드한다. */
  const [switching, setSwitching] = React.useState(false);
  const [sheetHeight, setSheetHeight] = React.useState(0);

  const panelRefs = React.useRef(new Map<PanelKey, HTMLDivElement | null>());
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const disarmTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * `openPanel` 의 동기 미러. 「이미 열려 있었나」를 **렌더를 기다리지 않고** 읽어야
   * 진입 슬라이드 억제 여부를 그 자리에서 정할 수 있다. state 를 읽으면 같은 틱에 연속으로
   * 들어오는 호버에서 한 박자 늦은 값을 본다.
   */
  const openPanelRef = React.useRef<PanelKey | null>(null);

  /** 패널을 미리 올려 둔다(아직 열지는 않는다). 열 때 높이를 잴 수 있게 하는 준비 동작. */
  const arm = React.useCallback(() => {
    if (disarmTimer.current) {
      clearTimeout(disarmTimer.current);
      disarmTimer.current = null;
    }
    setArmed(true);
  }, []);

  const cancelClose = React.useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const close = React.useCallback(() => {
    cancelClose();
    openPanelRef.current = null;
    setOpenPanel(null);
    setSheetHeight(0);
    // 트랜지션(160ms)이 끝난 뒤에 내린다 — 바로 내리면 페이드가 잘린다.
    if (disarmTimer.current) clearTimeout(disarmTimer.current);
    disarmTimer.current = setTimeout(() => setArmed(false), 400);
    // ⚠️ 슬라이드 억제를 반드시 되돌린다. 남겨 두면 다음에 **닫힘 상태에서 열 때**
    //    진입 슬라이드가 사라진다.
    setSwitching(false);
  }, [cancelClose]);

  const open = React.useCallback(
    (key: PanelKey) => {
      cancelClose();
      arm();
      setSwitching(openPanelRef.current !== null);
      openPanelRef.current = key;
      setOpenPanel(key);
      setSheetHeight(panelRefs.current.get(key)?.offsetHeight ?? 0);
    },
    [arm, cancelClose],
  );

  const closeSoon = React.useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(close, CLOSE_DELAY_MS);
  }, [cancelClose, close]);

  // 언마운트 시 타이머 누수 방지.
  React.useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
      if (disarmTimer.current) clearTimeout(disarmTimer.current);
    },
    [],
  );

  // 라우트가 바뀌면 닫는다. 링크마다 `onClick` 을 달지 않아도 되고, 뒤로가기·프로그램
  // 이동에서도 열린 채로 남지 않는다.
  React.useEffect(() => {
    const handle = () => close();
    router.events.on("routeChangeStart", handle);
    return () => router.events.off("routeChangeStart", handle);
  }, [router.events, close]);

  // 스크롤하면 닫는다.
  // ⚠️ 이게 없으면 **열린 패널이 헤더보다 오래 산다.** 스티키 헤더는 스크롤 다운에서
  //    숨는데(`GamesPageTemplate` 등), 패널은 헤더의 absolute 자식이라 헤더가 숨어도
  //    화면에 남아 아래 콘텐츠의 클릭을 먹는다 — docs/pitfalls.md #36(스크롤로 숨는 스티키 헤더)가 잠근 그 결함이다.
  React.useEffect(() => {
    if (!openPanel) return;
    const onScroll = () => close();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [openPanel, close]);

  // Esc 로 닫기 (키보드 접근성).
  React.useEffect(() => {
    if (!openPanel) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openPanel, close]);

  // 반응형·웹폰트 로드 후 패널 높이가 바뀐다. 열려 있는 동안 재측정해 시트가 따라가게 한다.
  // `document.fonts.ready` 는 폰트가 늦게 붙어 콘텐츠가 한 줄 늘어나는 경우용.
  React.useLayoutEffect(() => {
    if (!openPanel) return;
    const node = panelRefs.current.get(openPanel);
    if (!node) return;

    // `open()` 시점엔 패널이 방금 마운트돼 높이가 0 일 수 있다. 여기서 실측으로 덮는다.
    const sync = () => setSheetHeight(node.offsetHeight);
    sync();

    const observer = new ResizeObserver(sync);
    observer.observe(node);
    window.addEventListener("resize", sync);
    document.fonts?.ready.then(sync).catch(() => {});

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [openPanel]);

  return (
    <NavigationMenuContext.Provider
      value={{
        menus: navigationMenus,
        activeMenuName,
        openPanel,
        open,
        close,
        closeSoon,
        cancelClose,
        arm,
        armed,
        switching,
        sheetHeight,
        panelRefs,
      }}
    >
      {children}
    </NavigationMenuContext.Provider>
  );
}

/**
 * **헤더 아래로 돌출해 네비 통로를 덮는 요소**가 스프레드해야 하는 핸들러.
 *
 * 시트·패널은 `mouseenter` 로 닫힘 타이머를 취소한다. 그 사이에 낀 요소(쿠폰 말풍선)가
 * 그걸 안 하면, 포인터가 트리거 → 시트로 내려가다 그 요소 위에서 **네비도 시트도 아닌
 * 상태**가 되어 120ms 뒤 닫힌다(docs/pitfalls.md #32 헤더 자손 돌출 hover dead zone).
 *
 * 실측(1280/de, 이 배선 없이): 말풍선 `1027..1260`(아래끝 151) · 헤더 아래끝 137 →
 * 2px 씩 내려가며 **`y=143` 에서 닫혔다**. fr 도 같은 지점.
 *
 * 이 다리를 놓으면 **말풍선을 숨기지 않아도** 통로가 살아 있다 — 쿠폰 알림과 드롭다운이
 * 동시에 보인다. 「관여 중 숨기기」보다 이쪽이 낫다(알림을 잃지 않는다).
 */
export function useNavCorridorBridge() {
  const { cancelClose, closeSoon } = useNavigationMenu();
  return { onMouseEnter: cancelClose, onMouseLeave: closeSoon };
}

/**
 * 1차 메뉴 `target` → 앵커 속성. **두 분기(패널 있음·없음)가 같이 쓴다.**
 *
 * 한쪽만 배선하면 같은 설정이 분기마다 다르게 동작한다 — 패널 없는 메뉴에 `_blank` 를 적으면
 * 모바일(`menu-view.tsx` 의 `WithoutContent`)은 새 탭, 데스크톱은 같은 탭이 된다(#5841 R1).
 * 새 탭이면 `rel` 로 `window.opener` 를 끊는다.
 */
function linkTargetProps(target: Menu["target"]) {
  return {
    target,
    rel: target === "_blank" ? "noopener noreferrer" : undefined,
  };
}

/** 트리거 묶음. 좌/우로 나뉘어 **두 번** 렌더된다. */
export function NavigationMenuTriggers({ group }: { group: MenuGroup }) {
  const {
    menus,
    activeMenuName,
    openPanel,
    open,
    close,
    closeSoon,
    arm,
    cancelClose,
  } = useNavigationMenu();
  const { languageCode } = useLocale();
  const { t } = useTrans(["gnb"]);

  return (
    <nav
      data-nav-group={group}
      onMouseEnter={() => {
        cancelClose();
        arm();
      }}
      onMouseLeave={closeSoon}
      // 키보드 사용자는 포인터 없이 트리거에 닿는다. `focus` 는 버블링하지 않지만
      // React 의 `onFocus` 는 `focusin` 이라 자식 포커스에서도 실행된다.
      onFocus={arm}
    >
      <ul className="flex items-center justify-center">
        {menus
          .filter((menu) => menu.group === group)
          .map((menu) => {
            const isActive = menu.name === activeMenuName;
            const panel = menu.panel;
            const isOpen = !!panel && openPanel === panel;

            return (
              <li key={menu.name} className="relative">
                {panel ? (
                  /**
                   * ★**1차 메뉴 라벨 자체가 링크다** (#5514 — *"그냥 PC게임 누르면
                   * PC게임 overview 로 가게"*).
                   *
                   * 종전엔 `<button>` 이라 클릭이 **패널 토글**이었고, config 의 `href` 는
                   * 활성 판정에만 쓰였다(실측 2026-09-03 · 1440: 패널 있는 4개
                   * `Store`·`PC Games`·`VR Games`·`Support` 가 전부 `href` 없는 BUTTON,
                   * 패널 없는 `Streamer`·`Developer` 만 A 였다).
                   *
                   * ## 모바일은 건드리지 않는다 — 구조적으로 분리돼 있다
                   *
                   * 이 트리거 묶음은 호출처에서 `hidden desktop:block`(1280↑)이고, 그
                   * 아래는 `NavigationMenuSheet`(햄버거)가 담당한다. 그래서 「클릭=이동」이
                   * **모바일 아코디언을 빼앗지 않는다.**
                   *
                   * ## 패널을 여는 경로가 바뀐다 — 키보드를 대체해 둔다
                   *
                   * 종전 `onClick` 토글이 **터치·키보드의 유일한 열기 경로**였다. 클릭이
                   * 이동으로 바뀌므로 `onFocus` 를 더한다 — Tab 으로 라벨에 닿으면 패널이
                   * 열리고 Enter 로 이동한다. (`nav` 의 `onFocus={arm}` 은 **미리 올리기**만
                   * 해서 열지 않으므로 그것만으로는 부족하다.)
                   *
                   * 터치는 이 폭에서 소수이고, 탭하면 Overview 로 가는 것이 이 이슈의 요청
                   * 그대로다 — 하위 항목은 그 Overview 화면에서 다시 고를 수 있다.
                   */
                  <Link
                    href={menu.href}
                    locale={languageCode}
                    // 외부 목적지(Support → Support Hub)는 새 탭. 패널은 hover·focus 로 열리므로
                    // `target` 이 패널 열기를 막지 않는다(`navigation-menus.ts` 의 Support 주석).
                    {...linkTargetProps(menu.target)}
                    // 열림 상태는 이 속성으로만 표현한다(별도 상태 클래스 없음).
                    aria-expanded={isOpen}
                    onMouseEnter={() => open(panel)}
                    onFocus={() => open(panel)}
                    className={cn(
                      "flex items-center gap-1 px-6 py-6.5",
                      isActive ? "font-600" : "font-400 hover:font-600",
                    )}
                  >
                    <BoldSafeLabel dotted={menu.dotted} labelKey={panel}>
                      {t(menu.name)}
                    </BoldSafeLabel>
                    {/* ★시안은 `icons/arrows/arrow_drop_down` — **속이 찬 삼각형**이다
                        (`29212:35731`). `ChevronDownIcon`(꺾쇠 두 획)과 **다른 글리프**라
                        바꾼다. 시안은 24px 박스지만 기존 컴포넌트가 16px 박스에 같은
                        글리프를 담고 있어(1.5배 하면 leaf 원점이 `(8.2296, 10.2504)` 로
                        시안 패스 시작점과 일치) **두 벌 만들지 않고 재사용**한다. */}
                    <ArrowDropDownIcon
                      aria-hidden
                      className={cn(
                        // 시안 `29212:35731` 의 아이콘 인스턴스가 **24×24** 다. 컴포넌트 기본값
                        // `h-4 w-4` 는 **푸터 기준**이라(푸터 시안 `29889:102788` 이 16×16)
                        // 여기서 덮는다 — 같은 글리프를 두 크기로 쓴다.
                        "duration-[180ms] size-6 transition-transform",
                        isOpen && "rotate-180",
                      )}
                    />
                  </Link>
                ) : (
                  <Link
                    href={menu.href}
                    locale={languageCode}
                    {...linkTargetProps(menu.target)}
                    // 트리거가 아닌 헤더 요소로 커서가 가면 닫는다.
                    onMouseEnter={closeSoon}
                    className={cn(
                      "flex items-center px-6 py-6.5",
                      isActive ? "font-600" : "font-400 hover:font-600",
                    )}
                  >
                    <BoldSafeLabel dotted={menu.dotted}>
                      {t(menu.name)}
                    </BoldSafeLabel>
                  </Link>
                )}
              </li>
            );
          })}
      </ul>
    </nav>
  );
}

/** 공유 시트 + 패널 4개. 바 루트에 **한 번만** 렌더한다. */
export function NavigationMenuPanels() {
  const {
    menus,
    openPanel,
    close,
    closeSoon,
    cancelClose,
    armed,
    switching,
    sheetHeight,
    panelRefs,
  } = useNavigationMenu();

  const panelMenus = menus.filter(
    (menu): menu is Menu & { panel: PanelKey } => !!menu.panel,
  );

  return (
    <>
      {/* 유일한 "면". 배경·보더·그림자·높이 트랜지션을 여기만 갖는다.
          패널에는 배경·그림자를 주지 않는다 — 주면 전환할 때 면이 두 겹으로 보인다. */}
      <div
        aria-hidden
        // 회귀 스펙이 「시트가 닫히지 않고 높이만 변한다」를 좌표로 확인한다.
        // `toBeVisible()` 로는 이 축을 못 본다.
        data-testid="gnb-sheet"
        data-open={openPanel ? "1" : "0"}
        // 트랜지션을 인라인으로 두는 이유: 높이와 페이드의 **easing 이 서로 다르다**
        // (높이 `cubic-bezier(.2,.6,.2,1)` / 페이드 `ease-out`). Tailwind 유틸리티로는
        // 프로퍼티별 easing 을 표현할 수 없고, 임의값 `[transition:…]` 은 뒤에 오는
        // `duration-*` 와 조용히 충돌한다.
        // ⚠️ `prefers-reduced-motion` 은 그대로 유효하다 — globals.css 의 전역 블록이
        //    `transition-duration: 0.01ms !important` 이고 `!important` 는 인라인을 이긴다.
        style={{
          height: sheetHeight,
          transition:
            "height 160ms cubic-bezier(.2,.6,.2,1), opacity 160ms ease-out",
        }}
        className={cn(
          "absolute left-0 top-full z-[25] w-full",
          "border-b border-coolgray-20 bg-white shadow-[0_12px_24px_rgba(0,0,0,0.08)]",
          "pointer-events-none opacity-0",
          "data-[open=1]:opacity-100",
        )}
      />

      {/* 네비에 들어와 있는 동안 패널 4개를 **한꺼번에** 올린다. 하나만 올리면 「메뉴 간
          전환」에서 다음 패널의 높이를 못 재 시트가 0 으로 튄다. */}
      {armed &&
        panelMenus.map((menu) => {
          const isOpen = openPanel === menu.panel;

          return (
            <div
              key={menu.panel}
              ref={(node) => {
                panelRefs.current.set(menu.panel, node);
              }}
              data-panel={menu.panel}
              data-open={isOpen ? "1" : "0"}
              onMouseEnter={cancelClose}
              onMouseLeave={closeSoon}
              // 시트와 같은 이유로 인라인. 여기서는 **전환 종류에 따라 duration 이 달라진다**
              // (닫힘→열림 160ms / 열림→열림 120ms).
              style={{
                transition: switching
                  ? "opacity 120ms ease-out, transform 120ms ease-out, visibility 120ms"
                  : "opacity 160ms ease-out, transform 160ms ease-out, visibility 160ms",
              }}
              className={cn(
                "absolute left-0 top-full z-[25] w-full",
                // 닫힌 패널은 `visibility:hidden` + `pointer-events:none` 이라 스크린리더·탭
                // 순서에서 빠진다. `opacity:0` 만 주면 보이지 않는 링크에 탭 포커스가 들어간다.
                "pointer-events-none invisible opacity-0",
                "data-[open=1]:pointer-events-auto data-[open=1]:visible data-[open=1]:opacity-100",
                // 진입 슬라이드(-6px → 0)는 **닫힘→열림에서만**. 열림→열림 전환에 슬라이드를
                // 남기면 콘텐츠가 위아래로 튀어서 "닫고 다시 열린" 것처럼 읽힌다.
                switching
                  ? "translate-y-0"
                  : "-translate-y-1.5 data-[open=1]:translate-y-0",
              )}
            >
              <NavigationMenuContent menu={menu} onClose={close} />
            </div>
          );
        })}

      <Portal>
        <div
          data-state={openPanel ? "open" : "closed"}
          className="fixed left-0 top-0 z-20 h-full w-full bg-black/70 transition-opacity duration-300 data-[state=closed]:pointer-events-none data-[state=open]:pointer-events-auto data-[state=closed]:opacity-0"
        />
      </Portal>
    </>
  );
}

/**
 * 볼드 호버가 레이아웃을 밀지 않도록 **숨김 복제로 볼드 폭을 미리 확보**한다.
 * 없으면 호버할 때 1차 메뉴 전체가 좌우로 밀린다.
 */
const BoldSafeLabel = ({
  children,
  dotted,
  labelKey,
}: React.PropsWithChildren<{ dotted?: boolean; labelKey?: PanelKey }>) => (
  /* ⚠️ `text-base` 는 **바깥 span** 에 둔다. 숨김 복제본에만 빠지면 복제본이 부모 크기를
     상속해(16px/lh 24px) 라벨 박스가 18px → 24px 로 커지고, `py-6.5`(52px)와 합쳐 트리거가
     **76px** 이 된다. 바는 70px 이므로 위아래로 3px 씩 **헤더 밖으로 돌출**하고, 그 띠가
     네비에서 시트로 내려가는 포인터를 가로챈다(docs/pitfalls.md #32 · 실측으로 재현).
     이 프로젝트의 `text-base` 는 14px 다(16px 아님). */
  /**
   * ★`data-gnb-label` — **패널이 이 라벨의 x 를 읽는 계약**이다 (#5577).
   *
   * 이 바깥 span 의 left 가 곧 **보이는 라벨 텍스트의 left** 다(아래 절대배치 span 이
   * `left-0`). 2뎁스 패널이 트리거 라벨과 좌측 정렬하려면 그 x 가 필요한데, 고정
   * offset 으로는 안 된다 — 오른쪽 묶음이라 트리거 **우단만 고정**이고 라벨 x 는
   * 라벨 폭만큼 움직인다(2026-09-07 실측 1440: `Support` 1154 · `지원` **1185**,
   * 우단은 둘 다 1261).
   *
   * DOM 조회로 읽으므로 **셀렉터가 아니라 이 속성이 계약**이다. 지우면 패널이 종전
   * 우측 정렬로 조용히 되돌아간다 — `__tests__/v2-components/gnb-panel-label-align.test.tsx`
   * 가 양쪽을 함께 잠근다.
   */
  <span data-gnb-label={labelKey} className="relative block text-base">
    <span aria-hidden className="invisible block whitespace-nowrap font-600">
      {children}
    </span>
    <span className="absolute left-0 top-0 whitespace-nowrap">
      {children}
      <Dot visible={dotted} />
    </span>
  </span>
);
